import { useAPIEditorTools } from "@/components/contexts/api-editor-context-hooks";
import { EditorInputProps } from "@/components/module-api-editor/types";
import { DtoSchemaWithInfo, useSchemaDtos } from "@/hooks/use-dtos";
import {
  addComponentSchemaToDefinition,
  editComponentSchemaInDefinition,
  removeComponentSchemaFromDefinition,
} from "@/lib/editor-mutations/oas-components";
import { DefinitionNotDefinedError, NotFoundError } from "@/lib/errors";
import { OASComponentsObject, OASResponseObject, OASSchema } from "@/lib/types";
import { useCallback } from "react";

export type ComponentSchemaWithInfo = {
  schema: OASSchema;
  name: string;
  dtoSchemas: DtoSchemaWithInfo[];
};

export type ComponentResponseWithInfo = {
  idx?: number;
  responseName: string;
  responseObject: OASResponseObject;
};

export function useComponents(props: EditorInputProps) {
  const { oas, setNewEditorState, setActiveElement } = useAPIEditorTools(props);
  const { getDtoSchemasForSchema } = useSchemaDtos(props);
  if (!oas) throw new DefinitionNotDefinedError();

  const getComponentSchemas = useCallback((): ComponentSchemaWithInfo[] => {
    const entries = Object.entries(oas.components?.schemas || {});
    return entries
      .filter(([, schema]) => schema["x-fiddle-dto-info"] === undefined)
      .map(([componentName, schema], i) => {
        return {
          idx: i,
          schema,
          name: componentName,
          dtoSchemas: getDtoSchemasForSchema(componentName),
        };
      });
  }, [oas.components?.schemas, getDtoSchemasForSchema]);

  const getComponentSchemaOrError = useCallback(
    (modelName: string) => {
      const componentSchemas = getComponentSchemas();
      const found = componentSchemas.find((e) => e.name === modelName);
      if (!found) throw new NotFoundError("model");
      return found;
    },
    [getComponentSchemas]
  );

  const addComponentSchema = useCallback(
    ({ schema, name }: { schema: OASSchema; name: string }) => {
      setNewEditorState({
        document_type: props.value.document_type,
        data: addComponentSchemaToDefinition({
          definition: oas,
          name,
          schema,
        }),
      });
    },
    [setNewEditorState, oas, props.value.document_type]
  );

  const removeComponentSchema = useCallback(
    async (name: string) => {
      await setActiveElement(undefined);
      setNewEditorState({
        document_type: props.value.document_type,
        data: removeComponentSchemaFromDefinition({
          definition: oas,
          schemaName: name,
        }),
      });
    },
    [oas, setNewEditorState, setActiveElement, props.value.document_type]
  );

  const editComponentSchema = useCallback(
    async ({
      oldName,
      newName,
      schema,
    }: {
      oldName: string;
      newName: string;
      schema: OASSchema;
    }) => {
      setNewEditorState({
        document_type: props.value.document_type,
        data: editComponentSchemaInDefinition({
          oldName,
          newName,
          definition: oas,
          newComponentSchema: schema,
        }),
      });
      await setActiveElement({ type: "model", modelName: newName });
    },
    [oas, setNewEditorState, props.value.document_type, setActiveElement]
  );

  const componentsObject: OASComponentsObject = oas.components || {};

  return {
    getComponentSchemas,
    addComponentSchema,
    editComponentSchema,
    removeComponentSchema,
    getComponentSchemaOrError,
    componentsObject,
  };
}
